import React, { useState } from 'react';
import { geocodeAddress } from '../../services/geocode';
import { AppButton } from '../ui/AppButton';

const AREA_UNITS = ['m2', 'ft2'];

const RequiredMark = () => (
  <span aria-hidden="true" style={{ color: '#b5524b', marginLeft: '2px' }}>*</span>
);

/**
 * Building Add/Edit form with structured address, all DB fields, and Nominatim geocoding.
 */
export const BuildingForm = ({
  editing = false,
  formData,
  onChange,
  onSubmit,
  onCancel,
  submitting,
  buildingTypes,
  badgeOptions,
}) => {
  const [geocodeState, setGeocodeState] = useState({ status: 'idle', message: '' });

  const handleGeocode = async () => {
    setGeocodeState({ status: 'loading', message: '' });
    try {
      const result = await geocodeAddress(formData.address, formData.city, formData.country);
      onChange({ target: { name: 'latitude', value: String(result.lat) } });
      onChange({ target: { name: 'longitude', value: String(result.lng) } });
      setGeocodeState({ status: 'success', message: result.label });
    } catch (err) {
      setGeocodeState({ status: 'error', message: err.message });
    }
  };

  const hasCoords = formData.latitude && formData.longitude;

  return (
    <form onSubmit={onSubmit} className="building-form" noValidate>

      {/* ── Step 1: Identity ── */}
      <div className="building-form-section">
        <h3>Building Identity</h3>
        <div className="building-form-grid">
          <label htmlFor="bf-name">
            Building Name <RequiredMark />
            <input
              id="bf-name"
              name="name"
              value={formData.name}
              onChange={onChange}
              required
              placeholder="e.g. Pilot Office Athens"
            />
          </label>
          <label htmlFor="bf-type">
            Type
            <select id="bf-type" name="type" value={formData.type} onChange={onChange}>
              {buildingTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
          <label htmlFor="bf-year">
            Year Built
            <input
              id="bf-year"
              type="number"
              name="year_built"
              value={formData.year_built}
              onChange={onChange}
              min="1800"
              max="2100"
              placeholder="e.g. 1995"
            />
          </label>
          <label htmlFor="bf-floors">
            Number of Floors
            <input
              id="bf-floors"
              type="number"
              name="number_of_floors"
              value={formData.number_of_floors}
              onChange={onChange}
              min="1"
              placeholder="e.g. 5"
            />
          </label>
        </div>
      </div>

      {/* ── Step 2: Address ── */}
      <div className="building-form-section">
        <h3>Location</h3>
        <div className="building-form-grid">
          <label htmlFor="bf-address" className="building-form-grid--span2">
            Street Address <RequiredMark />
            <input
              id="bf-address"
              name="address"
              value={formData.address}
              onChange={(e) => { onChange(e); setGeocodeState({ status: 'idle', message: '' }); }}
              required
              placeholder="e.g. 25 Kifisias Ave"
            />
          </label>
          <label htmlFor="bf-city">
            City <RequiredMark />
            <input
              id="bf-city"
              name="city"
              value={formData.city}
              onChange={onChange}
              required
              placeholder="e.g. Athens"
            />
          </label>
          <label htmlFor="bf-state">
            State / Region
            <input
              id="bf-state"
              name="state"
              value={formData.state}
              onChange={onChange}
              placeholder="e.g. Attica"
            />
          </label>
          <label htmlFor="bf-zip">
            Zip / Postal Code
            <input
              id="bf-zip"
              name="zip_code"
              value={formData.zip_code}
              onChange={onChange}
              placeholder="e.g. 11523"
            />
          </label>
          <label htmlFor="bf-country">
            Country <RequiredMark />
            <input
              id="bf-country"
              name="country"
              value={formData.country}
              onChange={onChange}
              required
              placeholder="e.g. Greece"
            />
          </label>
        </div>

        <div className="geocode-row">
          <AppButton
            type="button"
            variant="secondary"
            size="sm"
            loading={geocodeState.status === 'loading'}
            onClick={handleGeocode}
            disabled={!formData.address && !formData.city}
          >
            {geocodeState.status === 'loading' ? 'Locating…' : hasCoords ? 'Re-locate on Map' : 'Locate on Map'}
          </AppButton>
          {geocodeState.status === 'success' && (
            <span className="geocode-feedback geocode-feedback--ok">
              ✓ {geocodeState.message}
            </span>
          )}
          {geocodeState.status === 'error' && (
            <span className="geocode-feedback geocode-feedback--err">
              ✗ {geocodeState.message}
            </span>
          )}
          {geocodeState.status === 'idle' && hasCoords && (
            <span className="geocode-feedback geocode-feedback--ok">
              ✓ Coordinates saved ({Number(formData.latitude).toFixed(4)}, {Number(formData.longitude).toFixed(4)})
            </span>
          )}
        </div>
      </div>

      {/* ── Step 3: Performance ── */}
      <div className="building-form-section">
        <h3>Performance Profile</h3>
        <div className="building-form-grid">
          <label htmlFor="bf-area">
            Total Area
            <input
              id="bf-area"
              type="number"
              name="total_area"
              value={formData.total_area}
              onChange={onChange}
              min="0"
              placeholder="e.g. 2500"
            />
          </label>
          <label htmlFor="bf-area-unit">
            Area Unit
            <select
              id="bf-area-unit"
              name="total_area_unit"
              value={formData.total_area_unit}
              onChange={onChange}
            >
              {AREA_UNITS.map((u) => (
                <option key={u} value={u}>{u === 'm2' ? 'm²' : 'ft²'}</option>
              ))}
            </select>
          </label>
          <label htmlFor="bf-occupancy">
            Occupancy (%)
            <input
              id="bf-occupancy"
              type="number"
              min="0"
              max="100"
              name="occupancy"
              value={formData.occupancy}
              onChange={onChange}
              placeholder="0–100"
            />
          </label>
          <label htmlFor="bf-performance">
            Performance (%)
            <input
              id="bf-performance"
              type="number"
              min="0"
              max="100"
              name="performance"
              value={formData.performance}
              onChange={onChange}
              placeholder="0–100"
            />
          </label>
          <label htmlFor="bf-badge">
            Status Badge
            <select id="bf-badge" name="badge" value={formData.badge} onChange={onChange}>
              {badgeOptions.map((opt) => (
                <option key={opt.label} value={opt.label}>{opt.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="building-form-actions">
        <AppButton type="button" variant="ghost" onClick={onCancel}>Cancel</AppButton>
        <AppButton type="submit" variant="primary" loading={submitting}>
          {submitting ? 'Saving…' : editing ? 'Save Changes' : 'Add Building'}
        </AppButton>
      </div>
    </form>
  );
};
